import { isValidBigDecimal, isZero } from "@solomon-apps/shared";
import { DEFAULT_SALT } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useMemo } from "react";

import { calculateSharePrice, getUnvestedAmount } from "@/services/staking/staking-calculations";
import { useStakeVaultState } from "@/stores/useStakeStore";
import { type UserUnstakingQueueEntry, useStakingVaultState } from "@/stores/useStakingSessionStore";

interface UseStakingVaultDerivedResult {
    /** sUSDv → USDv exchange rate (assets per share, vesting-adjusted).
     *  `null` while vault state is loading or degenerate. */
    exchangeRate: BigDecimal | null;
    /** Cooldown period in whole days — defaults to 7 until vault state loads. */
    cooldownDays: number;
    /** Sum of user's unstake-queue entries whose cooldown has matured.
     *  `0` when nothing is claimable or queue is unavailable. */
    availableToClaim: BigDecimal;
    /** Whether the user's unstaking queue has loaded yet. */
    isQueueLoaded: boolean;
    /** Raw queue entries — exposed for tables/lists that display them. */
    unstakingQueue: UserUnstakingQueueEntry[] | undefined;
}

/**
 * Derived vault + user-queue values shared by UnstakeTab / WithdrawTab
 * (and partially by StakeTab).
 *
 * All three tabs recompute the same `exchangeRate`, `cooldownDays`, and
 * `availableToClaim` from the store — extracting here removes ~50 lines
 * of duplicate `useMemo` blocks across the three components.
 */
export function useStakingVaultDerived(): UseStakingVaultDerivedResult {
    const vault = useStakeVaultState(DEFAULT_SALT);
    const userStakingState = useStakingVaultState(DEFAULT_SALT);

    const exchangeRate = useMemo(() => {
        if (
            !vault.isStakeStateLoaded ||
            !isValidBigDecimal(vault.stakingTokenSupply) ||
            !isValidBigDecimal(vault.totalAssets) ||
            isZero(vault.stakingTokenSupply) ||
            isZero(vault.totalAssets)
        ) {
            return null;
        }

        if (
            !isValidBigDecimal(vault.vestingAmount) ||
            vault.lastDistributionTime === undefined ||
            vault.vestingPeriod === undefined
        ) {
            return vault.totalAssets.divide(vault.stakingTokenSupply);
        }

        return calculateSharePrice(
            vault.totalAssets,
            getUnvestedAmount(vault.vestingAmount, vault.lastDistributionTime, vault.vestingPeriod),
            vault.stakingTokenSupply
        );
    }, [vault]);

    const cooldownDays =
        vault.isStakeStateLoaded && vault.cooldown !== undefined ? Math.ceil(vault.cooldown / 86400) : 7;

    const availableToClaim = useMemo(() => {
        if (!userStakingState.userUnstakingQueue) return new BigDecimal(0);

        const now = Math.floor(Date.now() / 1000);
        let total = new BigDecimal(0);
        for (const entry of userStakingState.userUnstakingQueue) {
            if (entry.timestamp === 0 || entry.timestamp <= now) {
                total = total.add(entry.amount);
            }
        }
        return total;
    }, [userStakingState.userUnstakingQueue]);

    return {
        exchangeRate,
        cooldownDays,
        availableToClaim,
        isQueueLoaded: userStakingState.isUserUnstakingQueueLoaded,
        unstakingQueue: userStakingState.userUnstakingQueue
    };
}
